/**
 * PDOFitDelta — strategy-vs-execution comparison.
 *
 * Renders three buckets (matches / gaps / surprises) of PDO concepts derived
 * from the linked brief angle's driving_pdo_factors and the creative review's
 * linked_concepts. Gaps are clickable to open the linked brief at the
 * relevant section (Phase 3 wires the click target).
 */

import type { LinkedConcept, PDOConceptStatus, PDOFitConcept, WorkspaceBriefAngle } from './types';
import { computePDOFitDelta } from './utils/pdoFitDelta';

interface Props {
  briefAngle: WorkspaceBriefAngle | null;
  linkedConcepts: LinkedConcept[];
  onGapClick?: (concept: PDOFitConcept) => void;
}

export function PDOFitDelta({ briefAngle, linkedConcepts, onGapClick }: Props) {
  if (!briefAngle && linkedConcepts.length === 0) {
    return <PDOFitDeltaEmpty reason="no_brief_angle" />;
  }
  if (!briefAngle) {
    return <PDOFitDeltaEmpty reason="no_brief_angle" />;
  }

  const concepts = computePDOFitDelta(briefAngle, linkedConcepts);
  if (concepts.length === 0) {
    return <PDOFitDeltaEmpty reason="no_data" />;
  }

  const matches = concepts.filter((c) => c.status === 'match');
  const gaps = concepts.filter((c) => c.status === 'gap');
  const surprises = concepts.filter((c) => c.status === 'surprise');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
          Strategy vs. Execution
        </p>
        <div className="flex items-center gap-3 text-[10px] font-mono">
          <LegendChip status="match" count={matches.length} />
          <LegendChip status="gap" count={gaps.length} />
          <LegendChip status="surprise" count={surprises.length} />
        </div>
      </div>

      <SummaryLine matches={matches.length} gaps={gaps.length} surprises={surprises.length} />

      <div className="space-y-2">
        {concepts.map((concept) => (
          <PDOConceptRow
            key={`${concept.status}:${concept.concept_id}`}
            concept={concept}
            onClick={
              concept.status === 'gap' && onGapClick ? () => onGapClick(concept) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

function LegendChip({ status, count }: { status: PDOConceptStatus; count: number }) {
  const config: Record<PDOConceptStatus, { icon: string; color: string }> = {
    match: { icon: '✓', color: 'text-emerald-600' },
    gap: { icon: '✗', color: 'text-red-500' },
    surprise: { icon: '+', color: 'text-blue-500' },
  };
  const { icon, color } = config[status];
  return (
    <span className={`${color} flex items-center gap-0.5`}>
      {icon} {count} {status}
    </span>
  );
}

function SummaryLine({
  matches,
  gaps,
  surprises,
}: {
  matches: number;
  gaps: number;
  surprises: number;
}) {
  if (gaps === 0 && surprises === 0) {
    return (
      <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
        All {matches} intended PDO factor{matches === 1 ? '' : 's'} were detected. Strategy-execution alignment is strong.
      </p>
    );
  }
  const parts: string[] = [];
  if (gaps > 0) parts.push(`${gaps} intended factor${gaps > 1 ? 's' : ''} not detected`);
  if (surprises > 0)
    parts.push(`${surprises} unexpected signal${surprises > 1 ? 's' : ''} found`);
  return (
    <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      {parts.join(' · ')}.
      {gaps > 0 && ' The execution may have missed key strategic elements.'}
      {surprises > 0 && ' Surprise signals may represent new angle opportunities.'}
    </p>
  );
}

const STATUS_CONFIG: Record<
  PDOConceptStatus,
  { icon: string; bgColor: string; borderColor: string; iconColor: string; label: string }
> = {
  match: {
    icon: '✓',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    iconColor: 'text-emerald-600',
    label: 'Activated',
  },
  gap: {
    icon: '✗',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-500',
    label: 'Missing',
  },
  surprise: {
    icon: '+',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-500',
    label: 'Surprise',
  },
};

const EDGE_TYPE_COLOR: Record<string, string> = {
  DRIVES: 'text-teal-600 bg-teal-50 border-teal-200',
  INHIBITS: 'text-rose-600 bg-rose-50 border-rose-200',
  RESOLVES_CONCERN: 'text-blue-600 bg-blue-50 border-blue-200',
  REMOVES_BARRIER: 'text-purple-600 bg-purple-50 border-purple-200',
};

function PDOConceptRow({
  concept,
  onClick,
}: {
  concept: PDOFitConcept;
  onClick?: () => void;
}) {
  const cfg = STATUS_CONFIG[concept.status];
  const edgeColor =
    EDGE_TYPE_COLOR[concept.edge_type] ?? 'text-slate-600 bg-slate-50 border-slate-200';

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-3 ${cfg.bgColor} ${cfg.borderColor} ${
        onClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''
      }`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <span className={`text-sm font-bold flex-shrink-0 mt-0.5 ${cfg.iconColor}`}>
        {cfg.icon}
      </span>

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-navy-900">{concept.concept_name}</span>
          <span className="text-[9px] font-mono uppercase tracking-wider text-slate-400">
            {concept.category}
          </span>
          <span
            className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${edgeColor}`}
          >
            {concept.edge_type.replace(/_/g, ' ')}
          </span>
        </div>

        {concept.detected_evidence && (
          <p className="text-xs text-slate-600 leading-relaxed italic">
            &ldquo;{concept.detected_evidence}&rdquo;
          </p>
        )}

        <div className="flex items-center gap-4 text-[10px] font-mono text-slate-400">
          {concept.brief_confidence != null && (
            <span>
              intended: <strong className="text-slate-600">{String(concept.brief_confidence)}</strong>
            </span>
          )}
          {concept.detected_confidence != null && (
            <span>
              detected:{' '}
              <strong className="text-slate-600">
                {(concept.detected_confidence * 100).toFixed(0)}%
              </strong>
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span
          className={`text-[9px] font-mono uppercase tracking-wider ${cfg.iconColor}`}
        >
          {cfg.label}
        </span>
        {concept.status === 'gap' && onClick && (
          <span className="text-[9px] font-mono text-slate-400">Fix in brief →</span>
        )}
        {concept.status === 'surprise' && (
          <span className="text-[9px] font-mono text-slate-400">New angle?</span>
        )}
      </div>
    </div>
  );
}

function PDOFitDeltaEmpty({ reason }: { reason: 'no_brief_angle' | 'no_data' }) {
  const messages: Record<typeof reason, string> = {
    no_brief_angle:
      'Link this creative to a brief angle to see the strategy vs. execution comparison.',
    no_data: 'Run a creative review to generate PDO concept bindings for this creative.',
  };
  return (
    <div className="rounded-xl border border-dashed border-slate-200 p-5 text-center">
      <p className="text-xs text-slate-400">{messages[reason]}</p>
    </div>
  );
}
